import { useState, useEffect, useContext, useRef } from 'react';
import { getAllPatternsByCompanyId } from '../api/firebase/firestore/pattern/read';
import { PatternType } from '../types/pattern';
import { AuthContext } from '../contexts/auth';
import { isOperatorStaff } from '../utils/permission';
import { canViewSharedResource } from '../utils/groupAccess';
import { useCompanyId } from './useCompanyId';
import { useDetailMode } from './useDetailMode';

// グローバルキャッシュ（ページ遷移時にデータを保持）
let cachedPatterns: PatternType[] = [];
let hasFetchedOnce = false;

// キャッシュクリア関数（ログアウト時や代理モード切替時に呼び出す）
export const clearPatternListCache = (): void => {
  cachedPatterns = [];
  hasFetchedOnce = false;
};

export const usePatternList = (): {
  patterns: PatternType[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} => {
  // 初期値としてキャッシュを使用
  const [patterns, setPatterns] = useState<PatternType[]>(cachedPatterns);
  const [loading, setLoading] = useState<boolean>(!hasFetchedOnce);
  const [error, setError] = useState<string | null>(null);
  const { user, isLoading } = useContext(AuthContext);
  const { companyId: effectiveCompanyId, isProxyMode } = useCompanyId();
  const { isDetailView } = useDetailMode();
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const isInitialMount = useRef(true);
  const prevIsDetailView = useRef(isDetailView);
  // 直前に取得した対象企業（#3203）。
  // 代理モードの確立・解除で対象企業が変わったことを検知するために保持する。
  const prevCompanyIdForQuery = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    // 認証ロード中の場合は何もしない
    if (isLoading) {
      return;
    }

    // ユーザーが存在しない場合は空の配列を設定してローディング完了
    if (!user) {
      setPatterns([]);
      setLoading(false);
      return;
    }

    // 代理モードの場合は対象企業のパターンのみ取得
    // 運営スタッフ以外でcompany_idがない場合は空配列を返す
    if (!isProxyMode && !isOperatorStaff(user.role) && !user.company_id) {
      setPatterns([]);
      setLoading(false);
      return;
    }

    const fetchPatterns = async (): Promise<void> => {
      // 初回ロード時のみローディング表示（キャッシュがある場合はバックグラウンドで更新）
      if (!hasFetchedOnce) {
        setLoading(true);
      }
      setError(null);
      try {
        // 代理モード時は対象企業のパターンのみ取得
        // 通常時は運営スタッフ含め自社のパターンのみ取得
        const companyIdForQuery = isProxyMode ? (effectiveCompanyId ?? undefined) : user.company_id;

        if (!companyIdForQuery) {
          setPatterns([]);
          setLoading(false);
          return;
        }

        // 運営スタッフかつ詳細モードの場合は削除済みパターンも含めて取得
        const result = await getAllPatternsByCompanyId(companyIdForQuery, isDetailView);
        // 共有範囲によるアクセス制御フィルタ（#2804 Phase2）
        // 運営・企業管理者は全件、一般メンバーは閲覧可能なパターンのみ
        const visible = result.filter(pattern =>
          canViewSharedResource(
            { uid: user.uid, role: user.role, group_ids: user.group_ids },
            pattern,
          ),
        );
        setPatterns(visible);
        // グローバルキャッシュを更新
        cachedPatterns = visible;
        hasFetchedOnce = true;
      } catch (err) {
        setError('読取パターン一覧の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    // 初回マウント時、リフレッシュトリガー時、詳細モード切替時、対象企業の切替時に取得
    const detailModeChanged = prevIsDetailView.current !== isDetailView;
    if (detailModeChanged) {
      prevIsDetailView.current = isDetailView;
      clearPatternListCache();
    }

    // 対象企業が変わったら取得し直す（#3203）。
    // URL の ?proxy= から代理モードが後から確立した場合、マウント済みのこのフックは
    // isInitialMount=false・refreshTrigger=0・詳細モード変化なしのどれにも当てはまらず、
    // 運営企業のパターン一覧をキャッシュのまま表示し続けてしまう。
    const companyIdForQuery = isProxyMode ? effectiveCompanyId : (user.company_id ?? null);
    const companyChanged =
      prevCompanyIdForQuery.current !== undefined &&
      prevCompanyIdForQuery.current !== companyIdForQuery;
    if (companyChanged) {
      clearPatternListCache();
      setPatterns([]);
    }
    prevCompanyIdForQuery.current = companyIdForQuery;

    if (isInitialMount.current || refreshTrigger > 0 || detailModeChanged || companyChanged) {
      isInitialMount.current = false;
      fetchPatterns();
    }
  }, [user, isLoading, refreshTrigger, isProxyMode, effectiveCompanyId, isDetailView]);

  const refresh = async (): Promise<void> => {
    setRefreshTrigger(prev => prev + 1);
  };

  return { patterns, loading, error, refresh };
};
